import React from "react";
import { NavigationLink, NavLinkType } from "@makula/ui";
import { WorkspaceSwitcher } from "../../../shared/WorkspaceSwitcher";
import type { RootView } from "../../../shared/productTypes";

export type ActivePage =
  | "Assets"
  | "Products"
  | "Parts"
  | "Tickets"
  | "Work Orders"
  | "My Work Orders"
  | "Procedures"
  | "Scheduler"
  | "PM Events"
  | "Emails"
  | "Insights"
  | "Archive"
  | "Companies"
  | "Contacts"
  | "Portals"
  | "Quotation"
  | "Invoices"
  | "Contracts"
  | "AI Notetaker"
  | "AI Chats"
  | "Settings"
  | "Help Center"
  | "Design System";

interface SidebarProps {
  activePage: ActivePage;
  onNavigate: (page: ActivePage) => void;
  onNavigateRoot: (view: RootView) => void;
}

const Divider = () => (
  <div
    style={{
      height: 1,
      background: "var(--color-border-primary)",
      width: "100%",
      flexShrink: 0,
    }}
  />
);

function NavGroup({
  items,
  activePage,
  onNavigate,
}: {
  items: NavLinkType[];
  activePage: ActivePage;
  onNavigate: (page: ActivePage) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-3xs)",
        width: "100%",
        flexShrink: 0,
      }}
    >
      {items.map((type) => (
        <NavigationLink
          key={type}
          type={type}
          active={activePage === type}
          onClick={() => onNavigate(type as ActivePage)}
        />
      ))}
    </div>
  );
}

export function Sidebar({ activePage, onNavigate, onNavigateRoot }: SidebarProps) {
  const sidebarStyle: React.CSSProperties = {
    width: 224,
    height: "100vh",
    background: "var(--color-bg-accent)",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    padding: "var(--spacing-m) var(--spacing-l)",
    borderBottom: "1px solid var(--color-border-primary)",
    borderRight: "1px solid var(--color-border-primary)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-l)",
    flexShrink: 0,
  };

  const middleStyle: React.CSSProperties = {
    flex: "1 0 0",
    padding: "var(--spacing-l)",
    borderRight: "1px solid var(--color-border-primary)",
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-xs)",
    minHeight: 0,
  };

  const footerStyle: React.CSSProperties = {
    padding: "var(--spacing-m) var(--spacing-l)",
    borderTop: "1px solid var(--color-border-primary)",
    borderRight: "1px solid var(--color-border-primary)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-3xs)",
    flexShrink: 0,
  };

  return (
    <aside style={sidebarStyle}>
      {/* Workspace header */}
      <div style={headerStyle}>
        <WorkspaceSwitcher
          activeProductId="field-service"
          onSelectProduct={onNavigateRoot}
          onOpenDashboard={() => onNavigateRoot("home")}
        />
      </div>

      {/* Main scrollable nav */}
      <div style={middleStyle}>
        {/* Group 1: Core assets */}
        <NavGroup
          items={["Assets", "Products", "Parts"]}
          activePage={activePage}
          onNavigate={onNavigate}
        />
        <Divider />

        {/* Group 2: Operations */}
        <NavGroup
          items={[
            "Tickets",
            "Work Orders",
            "My Work Orders",
            "Procedures",
            "Scheduler",
            "PM Events",
            "Emails",
            "Insights",
            "Archive",
          ]}
          activePage={activePage}
          onNavigate={onNavigate}
        />
        <Divider />

        {/* Group 3: Business */}
        <NavGroup
          items={["Companies", "Contacts", "Portals", "Quotation", "Invoices", "Contracts"]}
          activePage={activePage}
          onNavigate={onNavigate}
        />
        <Divider />

        {/* Group 4: AI */}
        <NavGroup
          items={["AI Notetaker", "AI Chats"]}
          activePage={activePage}
          onNavigate={onNavigate}
        />
      </div>

      {/* Footer nav */}
      <div style={footerStyle}>
        <NavigationLink
          type="Settings"
          active={activePage === "Settings"}
          onClick={() => onNavigate("Settings")}
        />
        <NavigationLink
          type="Help Center"
          active={activePage === "Help Center"}
          onClick={() => onNavigate("Help Center")}
        />
      </div>
    </aside>
  );
}
